import { Dispatch, FC, MouseEventHandler, SetStateAction } from 'react';
import {
	Anchor,
	Box,
	Button,
	Flex,
	Modal,
	Popover,
	SimpleGrid,
	Text,
	Title,
	Tooltip,
	UnstyledButton,
} from '@mantine/core';
import { SlQuestion } from 'react-icons/sl';
import { useDisclosure } from '@mantine/hooks';
import { FormAnswerMap, FormOptions, QuantityPerDimensionForm } from '../productTypeForm/ProductTypeForm';
import { Answer, Question, QuestionType } from '@/remoteApi/types/form-template.types';
import { getFileSrc } from '@/utilities';
import classNames from 'classnames';
import styles from './ModalPicker.module.scss';
import Image from 'next/image';

interface Props {
	question: Question;
	answerId: string | null;
	handleChange: (
		key: keyof FormAnswerMap,
		value: FormAnswerMap[keyof FormAnswerMap]
	) => void;
	isErorr: boolean;
	hanldleOptionsChange: (
		key: keyof FormOptions,
		value: FormOptions[keyof FormOptions]
	) => void;
	setQuantityPerDimension: Dispatch<SetStateAction<QuantityPerDimensionForm>>;
}

export const ModalPicker: FC<Props> = ({
	question,
	answerId,
	handleChange,
	isErorr,
	hanldleOptionsChange,
	setQuantityPerDimension,
}) => {
	const [isOpen, modalControls] = useDisclosure();

	const selectesAnswer = question.answers.find(
		(answer) => answer.id === answerId
	);

	const handleClick =
		(answer: Answer): MouseEventHandler<HTMLButtonElement> =>
		() => {
			handleChange('answerId', answer.id);

			if (question.questionType === QuestionType.MODEL_PICKER_TYPE) {
				hanldleOptionsChange('minTotalSurface', answer.minTotalSurface);
				setQuantityPerDimension([]);
			}

			modalControls.close();
		};

	return (
		<>
			<Box mb="md">
				<Flex w="100%" justify="space-between">
					<Text>{question.name}</Text>
					{!!question.description && (
						<Popover width={300}>
							<Popover.Target>
								<Anchor
									c="black"
									lh={0}
									onClick={(e) => {
										e.preventDefault();
									}}
								>
									<SlQuestion />
								</Anchor>
							</Popover.Target>
							<Popover.Dropdown>
								<Text size="md">{question.description}</Text>
							</Popover.Dropdown>
						</Popover>
					)}
				</Flex>
				<UnstyledButton
					className={classNames(styles.button, {
						[styles.buttonError]: isErorr,
					})}
					p="md"
					onClick={modalControls.open}
					bg="white"
				>
					{!!selectesAnswer?.image && (
						<Image
							src={getFileSrc(selectesAnswer.image.path)}
							alt="Produkt"
							width={200}
							height={200}
						/>
					)}
					<Title order={3}>
						{selectesAnswer ? selectesAnswer.name : 'Kliknij aby wybrać'}
					</Title>
				</UnstyledButton>
			</Box>
			<Modal
				opened={isOpen}
				onClose={modalControls.close}
				title="Wybierz rodzaj kartonu"
			>
				{!!question.description && (
					<Text size="md" c="dimmed" mb="md">
						{question.description}
					</Text>
				)}
				<SimpleGrid
					cols={{
						base: 1,
						sm: 2,
						md: 3,
					}}
				>
					{question.answers.map((answer) => (
						<Flex key={answer.id} bg="gray.3" p="sm" gap="sm">
							{!!answer.image && (
								<Image
									src={getFileSrc(answer.image.path)}
									alt="Produkt"
									width={100}
									height={100}
								/>
							)}
							<Box
								style={{
									flexGrow: 1,
								}}
							>
								<Flex mb="sm" justify="space-between" w="100%">
									<Title order={4} c="black">
										{answer.name}
									</Title>
									{!!answer.description && (
										<Tooltip label={answer.description} multiline w={300}>
											<Anchor c="black" lh={0}>
												<SlQuestion size={14} />
											</Anchor>
										</Tooltip>
									)}
								</Flex>
								<Button size="xs" onClick={handleClick(answer)}>
									Wybieram
								</Button>
							</Box>
						</Flex>
					))}
				</SimpleGrid>
			</Modal>
		</>
	);
};
